import { useState } from "react";
import { DRINKS, formatNaira } from "@/data/menu";
import { Beer, Wine, GlassWater, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";

const SECTIONS = [
  { id: "soft", icon: GlassWater, label: "Soft Drinks", accent: "from-[#F2B441] to-[#E89B26]" },
  { id: "alcohol", icon: Beer, label: "Alcohol", accent: "from-[#344E41] to-[#1f3128]" },
];

export default function Drinks() {
  const [active, setActive] = useState("soft");
  const current = DRINKS[active];
  const { openDetails } = useCart();

  return (
    <section
      id="drinks"
      data-testid="drinks-section"
      className="py-20 sm:py-28 bg-[#F9F6F0]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 reveal">
          <div>
            <p className="text-[#D9534F] text-xs font-semibold tracking-[0.25em] uppercase">
              From the bar
            </p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">
              Drinks, served cold.
            </h2>
          </div>
          <div className="flex gap-2" data-testid="drinks-tabs">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setActive(s.id)}
                data-testid={`drinks-tab-${s.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                  active === s.id
                    ? "bg-[#2C2825] text-white border-[#2C2825]"
                    : "bg-white text-[#2C2825] border-[#E8E2D6] hover:border-[#2C2825]"
                }`}
              >
                <s.icon size={14} /> {s.label}
              </button>
            ))}
          </div>
        </div>

        <div
          key={active}
          data-testid={`drinks-panel-${active}`}
          className="mt-8 grid lg:grid-cols-5 gap-6 reveal in-view"
        >
          <div className="lg:col-span-2">
            <div
              className={`rounded-3xl bg-gradient-to-br ${SECTIONS.find((s) => s.id === active).accent} text-white p-8 min-h-[260px] flex flex-col justify-between`}
            >
              <div className="w-14 h-14 rounded-full bg-white/15 grid place-items-center">
                {active === "soft" ? <GlassWater size={26} /> : <Wine size={26} />}
              </div>
              <div>
                <p className="text-white/70 text-xs uppercase tracking-[0.25em]">
                  Section
                </p>
                <h3 className="font-display text-3xl font-extrabold mt-1">
                  {current.title}
                </h3>
                <p className="mt-3 text-white/85 text-sm max-w-xs">
                  Tap any item to add it to your cart. Stock varies — call
                  ahead for large orders.
                </p>
              </div>
            </div>
          </div>

          <ul className="lg:col-span-3 divide-y divide-[#E8E2D6] bg-white rounded-3xl border border-[#E8E2D6] px-2 sm:px-3 py-2">
            {current.items.map((item) => (
              <li
                key={item.name}
                data-testid={`drink-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <button
                  type="button"
                  onClick={() => openDetails(item)}
                  className="w-full text-left py-4 px-3 sm:px-4 rounded-2xl hover:bg-[#F9F6F0] transition-colors group"
                >
                  <div className="menu-row">
                    <span className="font-display font-bold text-lg text-[#2C2825]">
                      {item.name}
                    </span>
                    <span className="dots" />
                    <span className="font-semibold text-[#D9534F]">
                      {formatNaira(item.price)}
                    </span>
                  </div>
                  {item.desc && (
                    <p className="mt-1 text-sm text-[#2C2825]/65 max-w-md">
                      {item.desc}
                    </p>
                  )}
                  <span className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-wider uppercase text-[#C73E3A] opacity-0 group-hover:opacity-100 transition-opacity">
                    <ShoppingBag size={10} /> Tap to add
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
